import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, AlertTriangle, Clock } from "lucide-react";
import type { ComplianceScore } from "@/utils/complianceScore";

interface ComplianceScoreCardProps {
  score: ComplianceScore;
}

export function ComplianceScoreCard({ score }: ComplianceScoreCardProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 65) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return "bg-green-100";
    if (score >= 65) return "bg-yellow-100";
    return "bg-red-100";
  };

  const getStatusIcon = () => {
    switch (score.readinessStatus) {
      case "ready":
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case "watch":
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case "action":
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
    }
  };

  const getStatusText = () => {
    switch (score.readinessStatus) {
      case "ready":
        return "Ready - Excellent compliance health";
      case "watch":
        return "Watch - Good compliance with room for improvement";
      case "action":
        return "Action Required - Compliance needs attention";
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <span>Compliance Health Score</span>
              <Badge
                variant={
                  score.readinessStatus === "ready"
                    ? "default"
                    : score.readinessStatus === "watch"
                    ? "secondary"
                    : "destructive"
                }
              >
                {score.readinessStatus.toUpperCase()}
              </Badge>
            </CardTitle>
            <CardDescription>Overall compliance readiness index</CardDescription>
          </div>
          {getStatusIcon()}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Score Display */}
          <div className="flex items-center gap-6">
            <div
              className={`rounded-full p-6 ${getScoreBgColor(score.overallScore)}`}
            >
              <div
                className={`text-4xl font-medium ${getScoreColor(score.overallScore)}`}
              >
                {score.overallScore}
              </div>
            </div>
            <div className="flex-1">
              <Progress value={score.overallScore} className="h-3 mb-2" />
              <p className="text-sm text-muted-foreground">{getStatusText()}</p>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Tasks</p>
              <p className="text-2xl font-medium">{score.metrics.totalTasks}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Completed</p>
              <p className="text-2xl font-medium text-green-600">
                {score.metrics.completedTasks}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Overdue</p>
              <p className="text-2xl font-medium text-red-600">
                {score.metrics.overdueTasks}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

